import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";
import { getApiBaseUrl } from "../../services/httpServices";
import DashboardLayout from "../common/DashboardLayout";
import "./coursePlayer.css";

/**
 * Module-by-module course player.
 *
 * Read a module, pass its quiz, unlock the next. Progress is server-side: this
 * component asks for a module and renders what it is given, and a locked module
 * comes back 403 no matter what the browser does. Refreshing, closing the tab or
 * switching machines resumes where the learner left off.
 */

const LETTERS = ["A", "B", "C", "D", "E", "F"];

const api = () => getApiBaseUrl().replace(/\/+$/, "");
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function Sections({ module: mod, onStart }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [mod.moduleNumber]);

  // Locked modules arrive with sections withheld by the server.
  const sections = mod.sections || [];

  // A final exam is taken, not read. It used to render like any other module,
  // so a learner paged through a blank certificate template, notes written for
  // administrators, references and a disclaimer before reaching the questions.
  if (mod.isFinal) {
    return (
      <div className="cp-card">
        <div className="cp-eyebrow">Final step</div>
        <h3>{mod.title}</h3>
        <p className="cp-lead">
          This exam draws on material from all of the preceding modules.
          {mod.questionCount ? ` ${mod.questionCount} questions.` : ""} You can go back to
          any module and re-read it before you begin.
        </p>
        <div className="cp-row"><span />
          <button type="button" className="cp-btn" onClick={onStart}>Begin exam →</button>
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="cp-card">
        <h3>{mod.title}</h3>
        <p className="cp-muted">This module has no written content.</p>
        <div className="cp-row"><span />
          <button type="button" className="cp-btn" onClick={onStart}>Start assessment →</button>
        </div>
      </div>
    );
  }

  const s = sections[idx];
  const last = idx === sections.length - 1;
  const isScenario = String(s.heading || "").toLowerCase().startsWith("scenario");

  return (
    <div className="cp-card">
      <div className="cp-eyebrow">
        Module {mod.moduleNumber} · Section {idx + 1} of {sections.length}
      </div>
      <h3>{s.heading}</h3>
      {s.body ? <p className="cp-lead">{s.body}</p> : null}
      {(s.points || []).length > 0 && (
        isScenario
          ? <div className="cp-best">{(s.points || []).join(" ")}</div>
          : <ul className="cp-points">{(s.points || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
      )}
      <div className="cp-row">
        <button type="button" className="cp-btn ghost" disabled={idx === 0}
          onClick={() => setIdx((i) => i - 1)}>← Back</button>
        <div className="cp-dots">
          {sections.map((_, i) => (
            <i key={i} className={i < idx ? "on" : i === idx ? "cur" : ""}
              onClick={() => setIdx(i)} />
          ))}
        </div>
        <button type="button" className="cp-btn"
          onClick={() => (last ? onStart() : setIdx((i) => i + 1))}>
          {last ? "Start assessment →" : "Next →"}
        </button>
      </div>
    </div>
  );
}
Sections.propTypes = { module: PropTypes.object.isRequired, onStart: PropTypes.func.isRequired };

function Quiz({ moduleNumber, quiz, onSubmit, submitting }) {
  const [answers, setAnswers] = useState({});
  const [cur, setCur] = useState(0);
  useEffect(() => { setAnswers({}); setCur(0); }, [moduleNumber]);

  const qs = quiz.questions || [];
  const q = qs[cur];
  const all = qs.every((_, i) => answers[i] !== undefined);

  const pick = (i) => {
    setAnswers((a) => ({ ...a, [cur]: i }));
    if (cur < qs.length - 1) setTimeout(() => setCur((c) => c + 1), 220);
  };

  return (
    <div className="cp-card">
      <div className="cp-dots wide">
        {qs.map((_, i) => (
          <i key={i} className={answers[i] !== undefined ? "on" : i === cur ? "cur" : ""}
            onClick={() => setCur(i)} />
        ))}
      </div>
      <div className="cp-eyebrow">QUESTION {cur + 1} OF {qs.length}</div>
      <div className="cp-qtext">{q.question}</div>
      <div className="cp-opts">
        {(q.options || []).map((o, i) => (
          <div key={i} className={`cp-opt${answers[cur] === i ? " sel" : ""}`}
            onClick={() => pick(i)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") pick(i); }}>
            <span className="cp-k">{LETTERS[i]}</span><span>{o}</span>
          </div>
        ))}
      </div>
      <div className="cp-row">
        <button type="button" className="cp-btn ghost" disabled={cur === 0}
          onClick={() => setCur((c) => c - 1)}>← Back</button>
        <span className="cp-muted">
          {Object.keys(answers).length} of {qs.length} answered · {quiz.required} correct to pass
        </span>
        <button type="button" className="cp-btn green" disabled={!all || submitting}
          onClick={() => onSubmit(qs.map((_, i) => answers[i]))}>
          {submitting ? "Submitting…" : "Submit assessment →"}
        </button>
      </div>
    </div>
  );
}
Quiz.propTypes = {
  moduleNumber: PropTypes.number.isRequired, quiz: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired, submitting: PropTypes.bool
};
Quiz.defaultProps = { submitting: false };

function Results({ result, quiz, onRetry, onContinue, onReview }) {
  const { score, passed, correctCount, totalQuestions, required, results } = result;
  return (
    <div className="cp-card">
      <div className="cp-score">
        <div className="cp-em">{passed ? (correctCount === totalQuestions ? "🎉" : "✅") : "📚"}</div>
        <div className={`cp-n ${passed ? "ok" : "no"}`}>
          {correctCount} / {totalQuestions} · {score}%
        </div>
        <div className="cp-muted">
          {passed
            ? (correctCount === totalQuestions ? "Perfect score." : "You met the standard.")
            : `You need ${required} of ${totalQuestions} correct to continue.`}
        </div>
        <div className={`cp-pill ${passed ? "pass" : "fail"}`}>{passed ? "Passed" : "Not yet passed"}</div>
      </div>

      <div className="cp-review">
        {(results || []).map((r, i) => {
          const q = (quiz.questions || [])[i] || {};
          const opts = q.options || [];
          return (
            <div key={i} className={`cp-rv ${r.isCorrect ? "ok" : "no"}`}>
              <div className="cp-rvh">{r.isCorrect ? "✓ Correct" : "✗ Incorrect"}</div>
              <div className="cp-rvq">{i + 1}. {q.question}</div>
              {/* On a failed attempt the server withholds the correct answer and
                  the explanation, so the retake is not a formality. Show what
                  they chose and which were wrong; the answers come with a pass. */}
              {r.isCorrect ? (
                <div>{r.correctAnswer != null ? opts[r.correctAnswer] : "Correct"}</div>
              ) : (
                <div>
                  <b>You chose:</b> {r.userAnswer != null ? opts[r.userAnswer] : "(no answer)"}
                  {r.correctAnswer != null ? (
                    <><br /><b>Correct:</b> {opts[r.correctAnswer]}</>
                  ) : null}
                </div>
              )}
              {r.explanation ? <div className="cp-why">{r.explanation}</div> : null}
            </div>
          );
        })}
      </div>

      {!passed && (
        <div className="cp-gate">
          This module stays locked until you reach the required score. The questions you
          missed are marked above; review those sections and retake. The answers are shown
          once you pass, and the options are reshuffled on every attempt.
        </div>
      )}

      <div className="cp-row">
        <button type="button" className="cp-btn ghost" onClick={onReview}>Review sections</button>
        <span />
        {passed
          ? <button type="button" className="cp-btn green" onClick={onContinue}>Continue →</button>
          : <button type="button" className="cp-btn" onClick={onRetry}>Retake assessment →</button>}
      </div>
    </div>
  );
}
Results.propTypes = {
  result: PropTypes.object.isRequired, quiz: PropTypes.object.isRequired,
  onRetry: PropTypes.func.isRequired, onContinue: PropTypes.func.isRequired,
  onReview: PropTypes.func.isRequired
};

const CoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [current, setCurrent] = useState(null);
  const [mode, setMode] = useState("sections");
  const [quiz, setQuiz] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${api()}/admin/custom-training/courses/${courseId}/player`,
        { headers: authHeaders() });
      if (!data.success) { setError(data.error || "Could not load this course."); return; }
      setPlayer(data);
      setCurrent((c) => c ?? data.resumeAt);
    } catch (err) {
      const st = err?.response?.status;
      if (st === 409) {
        // Not a module course — send them to the document view instead.
        navigate(`/custom-training/pdf/${courseId}`, { replace: true });
        return;
      }
      setError(err?.response?.data?.error || "Could not load this course.");
    } finally { setLoading(false); }
  }, [courseId, navigate]);

  useEffect(() => { load(); }, [load]);

  async function startQuiz(n) {
    setError(""); setResult(null);
    try {
      const { data } = await axios.get(
        `${api()}/admin/custom-training/courses/${courseId}/modules/${n}/quiz`,
        { headers: authHeaders() });
      if (!data.success) { setError(data.error || "Could not load the assessment."); return; }
      setQuiz(data); setMode("quiz");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load the assessment.");
    }
  }

  async function restart() {
    if (!window.confirm(
      "Start the training again from module 1?\n\n" +
      "Your certification is not affected — it keeps its completion date and " +
      "expiry, and your previous results are kept."
    )) return;
    setRestarting(true); setError("");
    try {
      await axios.post(`${api()}/admin/custom-training/courses/${courseId}/restart`, {},
        { headers: authHeaders() });
      setCurrent(1); setMode("sections"); setResult(null); setQuiz(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not restart the course.");
    } finally { setRestarting(false); }
  }

  async function submit(answers) {
    setSubmitting(true); setError("");
    try {
      const { data } = await axios.post(
        `${api()}/admin/custom-training/courses/${courseId}/modules/${current}/submit`,
        { answers }, { headers: authHeaders() });
      if (!data.success) { setError(data.error || "Could not submit."); return; }
      setResult(data); setMode("results");
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not submit your answers.");
    } finally { setSubmitting(false); }
  }

  if (loading && !player) {
    return <DashboardLayout role="teacher" layoutType="topnav" showBreadcrumbs={false}>
      <div className="cp"><div className="cp-card"><p className="cp-muted">Loading course…</p></div></div>
    </DashboardLayout>;
  }
  if (error && !player) {
    return <DashboardLayout role="teacher" layoutType="topnav" showBreadcrumbs={false}>
      <div className="cp"><div className="cp-alert">{error}</div></div>
    </DashboardLayout>;
  }
  if (!player) return null;

  const mod = player.modules.find((m) => m.moduleNumber === current) || player.modules[0];
  const passedCount = player.modules.filter((m) => m.result && m.result.passed).length;

  return (
    <DashboardLayout role="teacher" layoutType="topnav" showBreadcrumbs={false}>
      <div className="cp">
        <div className="cp-top">
          <div>
            <h2>{player.course.title}</h2>
            <div className="cp-muted">
              {passedCount} of {player.modules.length} modules passed · {player.passingScore}% to pass each
            </div>
          </div>
          <button type="button" className="cp-btn ghost" onClick={() => navigate(-1)}>Exit</button>
        </div>

        <div className="cp-track">
          {player.modules.map((m) => {
            const done = m.result && m.result.passed;
            const active = m.moduleNumber === current;
            return (
              <button type="button" key={m.moduleNumber}
                className={`cp-step${done ? " done" : ""}${active ? " active" : ""}${m.unlocked ? "" : " locked"}`}
                disabled={!m.unlocked}
                title={m.unlocked ? m.title : "Finish the previous module first"}
                onClick={() => { if (m.unlocked) { setCurrent(m.moduleNumber); setMode("sections"); setResult(null); } }}>
                {done ? "✓" : m.unlocked ? m.moduleNumber : "🔒"}
              </button>
            );
          })}
        </div>

        {error ? <div className="cp-alert">{error}</div> : null}

        {player.completed && mode !== "quiz" && (() => {
          const c = player.certification || {};
          const dt = (d) => (d ? new Date(d).toLocaleDateString() : null);
          return (
            <div className="cp-done">
              <div>
                🎓 <b>Course complete.</b>{" "}
                {c.dateCompleted ? `Completed ${dt(c.dateCompleted)}.` : "Your certification has been recorded."}{" "}
                {c.renewByDate ? `Valid until ${dt(c.renewByDate)}.` : ""}
              </div>
              {c.renewalDue ? (
                <div className="cp-renew">
                  <b>Renewal is due.</b> Work through the training again and your certification
                  will renew when you finish it.
                </div>
              ) : c.daysUntilRenewal != null ? (
                <div className="cp-muted" style={{ marginTop: 6 }}>
                  You can retake this any time to refresh your memory — it will not change your
                  certification or its expiry. Renewal opens {c.renewalWindowDays} days before
                  it expires, in about {c.daysUntilRenewal} days.
                </div>
              ) : null}
              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="cp-btn"
                  onClick={() => navigate(`/custom-training/certificate/${courseId}`)}>
                  View certificate
                </button>
                <button type="button" className="cp-btn ghost" disabled={restarting} onClick={restart}>
                  {restarting ? "Starting over…" : c.renewalDue ? "Start renewal →" : "Retake training"}
                </button>
              </div>
            </div>
          );
        })()}

        {mode === "sections" && <Sections module={mod} onStart={() => startQuiz(mod.moduleNumber)} />}
        {mode === "quiz" && quiz && (
          <Quiz moduleNumber={mod.moduleNumber} quiz={quiz} onSubmit={submit} submitting={submitting} />
        )}
        {mode === "results" && result && result.courseCompleted && (
          <div className={result.renewed ? "cp-done" : "cp-practice"}>
            <div>
              {result.renewed
                ? "🎓 Certification renewed. Your new expiry date has been recorded."
                : result.practice
                  ? "✅ Training complete. This was a practice run — your certification and its expiry are unchanged."
                  : "🎓 Course complete. Your certification has been recorded."}
            </div>
            <div style={{ marginTop: 10 }}>
              <button type="button" className="cp-btn"
                onClick={() => navigate(`/custom-training/certificate/${courseId}`)}>
                View certificate
              </button>
            </div>
          </div>
        )}
        {mode === "results" && result && quiz && (
          <Results
            result={result} quiz={quiz}
            onReview={() => setMode("sections")}
            onRetry={() => startQuiz(mod.moduleNumber)}
            onContinue={() => {
              const next = player.modules.find((m) => m.moduleNumber === result.nextModule);
              if (next) { setCurrent(next.moduleNumber); setMode("sections"); setResult(null); }
              else { setMode("sections"); }
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default CoursePlayer;
